import { ordersApi } from '../api/orders';
import { profileApi } from '../api/profile';
import secureLocalStorage from 'react-secure-storage';
import { HttpRequest } from '../../components/HttpRequest';

/**
 * Account Service - Handles all business logic for Account page
 * Separates concerns between API calls (api/) and business logic (services/)
 */
class AccountService {
  /**
   * Fetch customer profile and return formatted data
   */
  async fetchCustomerProfile() {
    try {
      const customerId = secureLocalStorage.getItem('customerId');

      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID not found',
          data: null
        };
      }

      const result = await profileApi.getCustomerProfile(customerId);

      if (result.success) {
        return {
          success: true,
          data: {
            firstName: result.data?.firstName || '',
            lastName: result.data?.lastName || '',
            email: result.data?.email || '',
            phone: result.data?.phone || '',
            dateOfBirth: result.data?.dateOfBirth || '',
            rawData: result.data // Keep raw data for reference
          }
        };
      }

      return {
        success: false,
        error: result.error || 'Failed to fetch profile',
        data: null
      };
    } catch (error) {
      console.error('Error in fetchCustomerProfile:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch profile',
        data: null
      };
    }
  }

  /**
   * Fetch customer orders with pagination
   */
  async fetchCustomerOrders(page = 1, limit = 10, orderStatus = '') {
    try {
      const customerId = secureLocalStorage.getItem('customerId');

      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID not found',
          orders: [],
          total: 0
        };
      }

      const result = await ordersApi.getCustomerOrders(
        customerId,
        page,
        limit,
        orderStatus
      );

      if (result.success) {
        return {
          success: true,
          orders: result.data || [],
          total: result.total || 0
        };
      }

      return {
        success: false,
        error: result.error || 'Failed to fetch orders',
        orders: [],
        total: 0
      };
    } catch (error) {
      console.error('Error in fetchCustomerOrders:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch orders',
        orders: [],
        total: 0
      };
    }
  }

  /**
   * Fetch customer purchased e-vouchers
   */
  async fetchCustomerVouchers(page = 1, limit = 50) {
    try {
      const customerId = secureLocalStorage.getItem('customerId');

      if (!customerId) {
        return { success: false, error: 'Customer ID not found', vouchers: [], total: 0 };
      }

      const response = await HttpRequest({
        method: 'GET',
        url: `/api/v1/ecommerce/getzedecommercecustomerorder?customerId=${customerId}&page=${page}&limit=${limit}`
      });

      if (response?.status === 200) {
        return {
          success: true,
          vouchers: response.response?.customerEvouchers || [],
          total: response.response?.vouchersCount || 0
        };
      }

      return { success: false, error: 'Failed to fetch vouchers', vouchers: [], total: 0 };
    } catch (error) {
      console.error('Error in fetchCustomerVouchers:', error);
      return { success: false, error: error.message || 'Failed to fetch vouchers', vouchers: [], total: 0 };
    }
  }

  /**
   * Update customer profile
   */
  async updateProfile(firstName, lastName, phone, email, dateOfBirth) {
    try {
      const customerId = secureLocalStorage.getItem('customerId');

      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID not found'
        };
      }

      // Validate required fields
      if (!firstName?.trim() || !lastName?.trim() || !email?.trim()) {
        return {
          success: false,
          error: 'First name, last name, and email are required'
        };
      }

      const profileData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone?.trim() || '',
        dateOfBirth: dateOfBirth || ''
      };

      const result = await profileApi.updateCustomerProfile(
        customerId,
        profileData
      );

      if (result.success) {
        return {
          success: true,
          message: 'Profile updated successfully',
          data: result.data
        };
      }

      return {
        success: false,
        error: result.message || 'Failed to update profile'
      };
    } catch (error) {
      console.error('Error in updateProfile:', error);
      return {
        success: false,
        error: error.message || 'Failed to update profile'
      };
    }
  }

  /**
   * Change customer password with validation
   */
  async changePassword(currentPassword, newPassword, confirmPassword) {
    try {
      const email = secureLocalStorage.getItem('customerEmail') || '';

      // Validation
      const errors = [];

      if (!currentPassword) {
        errors.push('Current password is required');
      }

      if (!newPassword) {
        errors.push('New password is required');
      }

      if (newPassword && newPassword.length < 4) {
        errors.push('Password must be at least 4 characters');
      }

      if (!confirmPassword) {
        errors.push('Confirm password is required');
      }

      if (newPassword && confirmPassword && newPassword !== confirmPassword) {
        errors.push('Passwords do not match');
      }

      if (errors.length > 0) {
        return {
          success: false,
          error: errors.join(', ')
        };
      }

      const result = await profileApi.changePassword(
        currentPassword,
        newPassword,
        email
      );

      if (result.success) {
        return {
          success: true,
          message: 'Password changed successfully'
        };
      }

      return {
        success: false,
        error: result.message || 'Failed to change password'
      };
    } catch (error) {
      console.error('Error in changePassword:', error);
      return {
        success: false,
        error: error.message || 'Failed to change password'
      };
    }
  }

  /**
   * Add new delivery address with validation
   */
  async addAddress(addressData) {
    try {
      const customerId = secureLocalStorage.getItem('customerId');

      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID not found'
        };
      }

      // Validation
      const errors = {};

      if (!addressData.address?.trim()) {
        errors.address = 'Address is required';
      }

      if (!addressData.city?.trim()) {
        errors.city = 'City is required';
      }

      if (!addressData.county?.trim()) {
        errors.county = 'County is required';
      }

      if (!addressData.postalCode?.trim()) {
        errors.postalCode = 'Postal code is required';
      }

      if (Object.keys(errors).length > 0) {
        return {
          success: false,
          errors
        };
      }

      const payload = {
        address: addressData.address.trim(),
        city: addressData.city.trim(),
        county: addressData.county.trim(),
        postalCode: addressData.postalCode.trim(),
        apartment: addressData.apartment?.trim() || ''
      };

      const result = await profileApi.addAddress(customerId, payload);

      if (result.success) {
        return {
          success: true,
          message: 'Address added successfully',
          data: result.data
        };
      }

      return {
        success: false,
        error: result.message || 'Failed to add address'
      };
    } catch (error) {
      console.error('Error in addAddress:', error);
      return {
        success: false,
        error: error.message || 'Failed to add address'
      };
    }
  }

  /**
   * Update delivery address
   */
  async updateAddress(addressId, addressData) {
    try {
      if (!addressId) {
        return {
          success: false,
          error: 'Address ID is required'
        };
      }

      // Validation
      const errors = {};

      if (!addressData.address?.trim()) {
        errors.address = 'Address is required';
      }

      if (!addressData.city?.trim()) {
        errors.city = 'City is required';
      }

      if (!addressData.county?.trim()) {
        errors.county = 'County is required';
      }

      if (!addressData.postalCode?.trim()) {
        errors.postalCode = 'Postal code is required';
      }

      if (Object.keys(errors).length > 0) {
        return {
          success: false,
          errors
        };
      }

      const payload = {
        address: addressData.address.trim(),
        city: addressData.city.trim(),
        county: addressData.county.trim(),
        postalCode: addressData.postalCode.trim(),
        apartment: addressData.apartment?.trim() || ''
      };

      const result = await profileApi.updateAddress(addressId, payload);

      if (result.success) {
        return {
          success: true,
          message: 'Address updated successfully',
          data: result.data
        };
      }

      return {
        success: false,
        error: result.message || 'Failed to update address'
      };
    } catch (error) {
      console.error('Error in updateAddress:', error);
      return {
        success: false,
        error: error.message || 'Failed to update address'
      };
    }
  }

  /**
   * Delete delivery address
   */
  async deleteAddress(addressId) {
    try {
      if (!addressId) {
        return {
          success: false,
          error: 'Address ID is required'
        };
      }

      const result = await profileApi.deleteAddress(addressId);

      if (result.success) {
        return {
          success: true,
          message: 'Address deleted successfully'
        };
      }

      return {
        success: false,
        error: result.message || 'Failed to delete address'
      };
    } catch (error) {
      console.error('Error in deleteAddress:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete address'
      };
    }
  }

  /**
   * Fetch customer wishlist with pagination support
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 10)
   * @param {string} search - Search filter (optional)
   */
  async fetchWishlist(page = 1, limit = 10, search = '') {
    try {
      const customerId = secureLocalStorage.getItem('customerId');

      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID not found',
          items: [],
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0
        };
      }

      const options = { page, limit };
      if (search) {
        options.search = search;
      }

      const result = await profileApi.getWishlist(customerId, options);

      if (result.success) {
        // Map wishlist data to ensure imageUrl is set correctly
        const mappedItems = (result.data || []).map(item => {
          // Get image from available fields, skip empty/whitespace strings
          const imageUrl = (
            (item.imageUrl && item.imageUrl.trim()) ||
            (item.image && item.image.trim()) ||
            (item.featuredImage && item.featuredImage.trim()) ||
            (item.thumbailImagePath && item.thumbailImagePath.trim()) ||
            ''
          );

          return {
            ...item,
            imageUrl,
            // Ensure id is set correctly
            id: item.id || item._id || item.productId,
            // Ensure price is set
            price: item.price || item.productPrice || 0,
            // Ensure name is set
            name: item.name || item.productName || 'Product',
            // Ensure category is set
            category: item.category || 'General'
          };
        });

        return {
          success: true,
          items: mappedItems,
          total: result.total || 0,
          page: result.page || page,
          limit: result.limit || limit,
          totalPages: result.totalPages || 0
        };
      }

      return {
        success: false,
        error: result.error || 'Failed to fetch wishlist',
        items: [],
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0
      };
    } catch (error) {
      console.error('Error in fetchWishlist:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch wishlist',
        items: [],
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0
      };
    }
  }

  /**
   * Add product to wishlist with validation
   * Returns specific error types for duplicate and not found scenarios
   */
  async addToWishlist(productId) {
    try {
      const customerId = secureLocalStorage.getItem('customerId');

      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID not found',
          errorType: 'missing_customer'
        };
      }

      if (!productId) {
        return {
          success: false,
          error: 'Product ID is required',
          errorType: 'invalid_product'
        };
      }

      const result = await profileApi.addToWishlist(customerId, productId);

      if (result.success) {
        return {
          success: true,
          message: 'Added to wishlist',
          data: result.data
        };
      }

      // Handle specific error types
      if (result.status === 400) {
        return {
          success: false,
          error: result.error || result.message || 'Product already in wishlist',
          errorType: 'duplicate',
          status: result.status
        };
      }

      if (result.status === 404) {
        return {
          success: false,
          error: result.error || result.message || 'Product not found',
          errorType: 'not_found',
          status: result.status
        };
      }

      return {
        success: false,
        error: result.error || result.message || 'Failed to add to wishlist',
        status: result.status
      };
    } catch (error) {
      console.error('Error in addToWishlist:', error);
      return {
        success: false,
        error: error.message || 'Failed to add to wishlist',
        errorType: 'server_error'
      };
    }
  }

  /**
   * Remove product from wishlist with validation
   */
  async removeFromWishlist(productId) {
    try {
      const customerId = secureLocalStorage.getItem('customerId');

      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID not found',
          errorType: 'missing_customer'
        };
      }

      if (!productId) {
        return {
          success: false,
          error: 'Product ID is required',
          errorType: 'invalid_product'
        };
      }

      const result = await profileApi.removeFromWishlist(customerId, productId);

      if (result.success) {
        return {
          success: true,
          message: 'Removed from wishlist',
          data: result.data
        };
      }

      // Handle specific error type - not found
      if (result.status === 404) {
        return {
          success: false,
          error: result.error || result.message || 'Product not in wishlist',
          errorType: 'not_found',
          status: result.status
        };
      }

      return {
        success: false,
        error: result.error || result.message || 'Failed to remove from wishlist',
        status: result.status
      };
    } catch (error) {
      console.error('Error in removeFromWishlist:', error);
      return {
        success: false,
        error: error.message || 'Failed to remove from wishlist',
        errorType: 'server_error'
      };
    }
  }

  /**
   * Check if product is in wishlist
   */
  async checkWishlistItem(productId) {
    try {
      const customerId = secureLocalStorage.getItem('customerId');

      if (!customerId) {
        return {
          success: false,
          inWishlist: false,
          error: 'Customer ID not found'
        };
      }

      if (!productId) {
        return {
          success: false,
          inWishlist: false,
          error: 'Product ID is required'
        };
      }

      const result = await profileApi.checkWishlistItem(customerId, productId);

      if (result.success) {
        return {
          success: true,
          inWishlist: result.inWishlist || false,
          data: result.data
        };
      }

      return {
        success: false,
        inWishlist: false,
        error: result.error || 'Failed to check wishlist'
      };
    } catch (error) {
      console.error('Error in checkWishlistItem:', error);
      return {
        success: false,
        inWishlist: false,
        error: error.message || 'Failed to check wishlist'
      };
    }
  }

  /**
   * Clear entire customer wishlist
   */
  async clearWishlist() {
    try {
      const customerId = secureLocalStorage.getItem('customerId');

      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID not found'
        };
      }

      const result = await profileApi.clearWishlist(customerId);

      if (result.success) {
        return {
          success: true,
          message: result.message || 'Wishlist cleared successfully',
          itemsRemoved: result.data?.itemsRemoved || 0
        };
      }

      return {
        success: false,
        error: result.message || 'Failed to clear wishlist'
      };
    } catch (error) {
      console.error('Error in clearWishlist:', error);
      return {
        success: false,
        error: error.message || 'Failed to clear wishlist'
      };
    }
  }

  /**
   * Update email notification preferences
   */
  async updateEmailPreferences(preferences) {
    try {
      const customerId = secureLocalStorage.getItem('customerId');

      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID not found'
        };
      }

      const result = await profileApi.updateEmailPreferences(
        customerId,
        preferences
      );

      if (result.success) {
        return {
          success: true,
          message: 'Email preferences updated'
        };
      }

      return {
        success: false,
        error: result.message || 'Failed to update preferences'
      };
    } catch (error) {
      console.error('Error in updateEmailPreferences:', error);
      return {
        success: false,
        error: error.message || 'Failed to update preferences'
      };
    }
  }

  /**
   * Delete customer account with password verification
   */
  async deleteAccount(password) {
    try {
      const customerId = secureLocalStorage.getItem('customerId');

      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID not found'
        };
      }

      if (!password) {
        return {
          success: false,
          error: 'Password is required for account deletion'
        };
      }

      const result = await profileApi.deleteAccount(customerId, password);

      if (result.success) {
        // Clear secure storage on successful account deletion
        secureLocalStorage.clear();
        return {
          success: true,
          message: 'Account deleted successfully'
        };
      }

      return {
        success: false,
        error: result.message || 'Failed to delete account'
      };
    } catch (error) {
      console.error('Error in deleteAccount:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete account'
      };
    }
  }

  /**
   * Format order data for display
   */
  formatOrderForDisplay(order) {
    return {
      id: order._id,
      orderNumber: order.orderNumber || 'N/A',
      amount: order.amount || 0,
      date: order.createdAt
        ? new Date(order.createdAt).toLocaleDateString()
        : 'N/A',
      status: order.orderStatus || 'Pending',
      items: order.items || []
    };
  }

  /**
   * Format multiple orders for display
   */
  formatOrdersForDisplay(orders) {
    return orders.map((order) => this.formatOrderForDisplay(order));
  }

  /**
   * Validate profile form data
   */
  validateProfileForm(firstName, lastName, email, phone) {
    const errors = {};

    if (!firstName?.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!lastName?.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email is invalid';
    }

    if (phone?.trim() && !/^[0-9\s\-+()]+$/.test(phone)) {
      errors.phone = 'Phone number is invalid';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  /**
   * Toggle wishlist item - handles both API calls and localStorage
   * Automatically determines if user is authenticated
   * @param {Object} product - Product object
   * @returns {Object} Result with success status and message
   */
  async toggleWishlistItem(product) {
    try {
      const customerId = secureLocalStorage.getItem('customerId');
      const productId = product.id || product._id;

      if (!productId) {
        return {
          success: false,
          error: 'Product ID is required',
          inWishlist: false
        };
      }

      if (customerId) {
        // User is authenticated - use API
        // First check if product is in wishlist
        const checkResult = await profileApi.checkWishlistItem(customerId, productId);
        const isInWishlist = checkResult.success && checkResult.inWishlist;

        if (isInWishlist) {
          // Remove from wishlist
          const result = await profileApi.removeFromWishlist(customerId, productId);
          if (result.success) {
            // Dispatch custom event for Navbar to update
            window.dispatchEvent(new CustomEvent('wishlistUpdated'));
          }
          return {
            success: result.success,
            message: result.message || 'Removed from Wishlist',
            inWishlist: false,
            error: result.error
          };
        } else {
          // Add to wishlist
          const result = await profileApi.addToWishlist(customerId, productId);
          if (result.success) {
            // Dispatch custom event for Navbar to update
            window.dispatchEvent(new CustomEvent('wishlistUpdated'));
          }
          return {
            success: result.success,
            message: result.message || 'Added to Wishlist',
            inWishlist: result.success,
            error: result.error
          };
        }
      } else {
        // User is not authenticated - use localStorage
        const savedWishlist = localStorage.getItem('wishlist');
        const wishlistItems = savedWishlist ? JSON.parse(savedWishlist) : [];
        const isInWishlist = wishlistItems.some(item => (item.id || item._id) === productId);

        if (isInWishlist) {
          // Remove from wishlist
          const updatedWishlist = wishlistItems.filter(item => (item.id || item._id) !== productId);
          localStorage.setItem('wishlist', JSON.stringify(updatedWishlist));
          // Dispatch custom event for Navbar to update
          window.dispatchEvent(new CustomEvent('wishlistUpdated'));
          return {
            success: true,
            message: 'Removed from Wishlist',
            inWishlist: false
          };
        } else {
          // Add to wishlist
          wishlistItems.push(product);
          localStorage.setItem('wishlist', JSON.stringify(wishlistItems));
          // Dispatch custom event for Navbar to update
          window.dispatchEvent(new CustomEvent('wishlistUpdated'));
          return {
            success: true,
            message: 'Added to Wishlist',
            inWishlist: true
          };
        }
      }
    } catch (error) {
      console.error('Error toggling wishlist item:', error);
      return {
        success: false,
        error: error.message || 'Failed to toggle wishlist',
        inWishlist: false
      };
    }
  }
}

const accountService = new AccountService();
export default accountService;
